import {
	Modal,
	Button,
	Stack,
	Text,
	TextInput,
	Alert,
	Group,
	Card,
	Code,
	LoadingOverlay,
	Stepper,
	Box,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
	IconAlertCircle,
	IconCheck,
	IconDeviceMobile,
	IconQrcode,
	IconShieldCheck,
} from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { useBreakpoint } from "@/common/hooks/useBreakpoint";
import { useFreshAuth } from "../hooks/useFreshAuth";
import { useAmplifyAuth } from "./AmplifyAuthContext";
import { FreshAuthRequired } from "./FreshAuthRequired";
import { usePendingTOTPSecret } from "../hooks/usePendingTOTPSecret";

interface SetupAuthenticatorAppModalProps {
	opened: boolean;
	onClose: () => void;
	size?: "md" | "lg" | "xl";
	onSuccess?: () => void;
}

type SetupStep = "setup" | "verify" | "complete";

export function SetupAuthenticatorAppModal({
	opened,
	onClose,
	onSuccess,
	size = "md",
}: SetupAuthenticatorAppModalProps) {
	const [currentStep, setCurrentStep] = useState<SetupStep>("setup");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [totpCode, setTotpCode] = useState("");
	const [secretKey, setSecretKey] = useState<string | null>(null);
	const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
	const lessThanLg = useBreakpoint("lg");
	const {
		setUpTOTP,
		verifyTOTPSetup,
		updateMFAPreference,
		fetchMFAPreference,
	} = useAmplifyAuth();
	const freshAuth = useFreshAuth();
	const pendingTOTPSecret = usePendingTOTPSecret();

	useEffect(() => {
		if (pendingTOTPSecret.restoredSecretKey) {
			setSecretKey(pendingTOTPSecret.restoredSecretKey);
			setQrCodeUrl(buildTOTPQrCodeUrl(pendingTOTPSecret.restoredSecretKey));
			setCurrentStep("verify");
		}
	}, [pendingTOTPSecret.restoredSecretKey]);

	const handleSetupTOTP = async () => {
		try {
			setIsLoading(true);
			setError(null);

			if (!(await freshAuth.ensureFreshAuth())) {
				return;
			}

			const totpSetupDetails = await setUpTOTP();
			setSecretKey(totpSetupDetails.sharedSecret);
			setQrCodeUrl(buildTOTPQrCodeUrl(totpSetupDetails.sharedSecret));
			setCurrentStep("verify");
			pendingTOTPSecret.save(totpSetupDetails.sharedSecret);
		} catch (err) {
			console.error("Failed to setup TOTP:", err);
			setError(
				err instanceof Error
					? err.message
					: "Failed to setup authenticator app",
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyTOTP = async () => {
		if (!totpCode.trim()) {
			setError("Please enter the verification code");
			return;
		}

		try {
			setIsLoading(true);
			setError(null);

			if (!(await freshAuth.ensureFreshAuth())) {
				return;
			}

			await verifyTOTPSetup({ code: totpCode });
			const currentMFA = await fetchMFAPreference();
			const hasSMS = currentMFA.enabled?.includes("SMS") ?? false;
			if (hasSMS) {
				// When SMS and TOTP are both enabled and neither is preferred, Cognito
				// responds to sign-in with a SELECT_MFA_TYPE challenge, so the Amplify
				// Authenticator lets the user choose which method to use.
				await updateMFAPreference({
					sms: "NOT_PREFERRED",
					totp: "NOT_PREFERRED",
				});
			} else {
				await updateMFAPreference({ totp: "PREFERRED" });
			}

			setCurrentStep("complete");

			notifications.show({
				title: "Authenticator App Setup Complete",
				message: "Your authenticator app has been successfully configured",
				color: "green",
				icon: <IconCheck size={16} />,
			});

			setTimeout(() => {
				onSuccess?.();
				handleClose();
			}, 2000);
		} catch (err) {
			console.error("Failed to verify TOTP:", err);
			setError(
				err instanceof Error ? err.message : "Invalid verification code",
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		pendingTOTPSecret.clear();
		setCurrentStep("setup");
		setTotpCode("");
		setSecretKey(null);
		setQrCodeUrl(null);
		setError(null);
		setIsLoading(false);
		onClose();
	};

	const getStepperActiveStep = () => {
		switch (currentStep) {
			case "setup":
				return 0;
			case "verify":
				return 1;
			case "complete":
				return 2;
			default:
				return 0;
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title="Setup Authenticator App"
			size={size}
			closeOnClickOutside={false}
			closeOnEscape={!isLoading}
			styles={{
				content: {
					paddingBottom: "var(--safe-area-inset-bottom)",
					paddingLeft: "var(--safe-area-inset-left)",
					paddingRight: "var(--safe-area-inset-right)",
					backgroundColor: "var(--mantine-color-body)",
				},
			}}
			withCloseButton
			centered
		>
			{freshAuth.needsFreshAuth && <FreshAuthRequired />}
			<Box
				pos="relative"
				display={freshAuth.needsFreshAuth ? "none" : undefined}
			>
				<LoadingOverlay visible={isLoading} />

				<Stepper
					active={getStepperActiveStep()}
					orientation="horizontal"
					size="sm"
					mb="lg"
				>
					<Stepper.Step
						icon={<IconQrcode size={16} />}
						label="Setup"
						description="Generate QR code"
					/>
					<Stepper.Step
						icon={<IconDeviceMobile size={16} />}
						label="Verify"
						description="Enter code"
					/>
					<Stepper.Step
						icon={<IconShieldCheck size={16} />}
						label="Complete"
						description="Activated"
					/>
				</Stepper>

				{error && (
					<Alert
						icon={<IconAlertCircle size="1rem" />}
						title="Error"
						color="red"
						mb="md"
					>
						{error}
					</Alert>
				)}

				{currentStep === "setup" && (
					<Stack gap="md">
						<Text size="sm" c="dimmed">
							Set up an authenticator app to secure your account with time-based
							one-time passwords (TOTP).
						</Text>
						<Button onClick={handleSetupTOTP} loading={isLoading} fullWidth>
							Generate QR Code
						</Button>
					</Stack>
				)}

				{currentStep === "verify" && qrCodeUrl && secretKey && (
					<Stack gap="md">
						<Text size="sm" fw={500}>
							Step 1: Scan QR Code
						</Text>
						<Text size="sm" c="dimmed">
							Open your authenticator app (Google Authenticator, Authy, etc.)
							and scan this QR code:
						</Text>

						<Card withBorder p="md" bg="gray.0">
							<Stack align="center" gap="sm">
								<QRCodeSVG value={qrCodeUrl} size={250} />
								<Text size="xs" c="dimmed" ta="center" mt="lg">
									Can't scan? Manually enter this key:
								</Text>
								<Code
									style={{ wordBreak: "break-all" }}
									onClick={(e) => {
										const selection = window.getSelection();
										const range = document.createRange();
										range.selectNodeContents(e.currentTarget);
										selection?.removeAllRanges();
										selection?.addRange(range);
										navigator.clipboard.writeText(secretKey).then(() => {
											notifications.show({
												id: "copy-secret-key",
												title: "Copied to clipboard",
												message:
													"The secret key has been copied to your clipboard",
												color: "green",
												icon: <IconCheck size={16} />,
											});
										});
									}}
								>
									{secretKey}
								</Code>
							</Stack>
						</Card>

						<Text size="sm" fw={500} mt="md">
							Step 2: Enter Verification Code
						</Text>
						<Text size="sm" c="dimmed">
							Enter the 6-digit code from your authenticator app:
						</Text>

						<TextInput
							placeholder="000000"
							value={totpCode}
							onChange={(e) => setTotpCode(e.target.value)}
							maxLength={6}
							size="lg"
							styles={{
								input: {
									textAlign: "center",
									fontSize: "1.2rem",
									letterSpacing: "0.2em",
								},
							}}
						/>

						<Group justify="space-between" mt="lg">
							<Button
								variant="outline"
								onClick={() => setCurrentStep("setup")}
								disabled={isLoading}
							>
								Back
							</Button>
							<Button
								onClick={handleVerifyTOTP}
								loading={isLoading}
								disabled={totpCode.length !== 6}
							>
								Verify & Activate
							</Button>
						</Group>
					</Stack>
				)}

				{currentStep === "complete" && (
					<Stack gap="md" align="center">
						<IconCheck size={48} color="green" />
						<Text size="lg" fw={500} ta="center">
							Authenticator App Setup Complete!
						</Text>
						<Text size="sm" c="dimmed" ta="center">
							Your account is now secured with two-factor authentication using
							your authenticator app.
						</Text>
					</Stack>
				)}
			</Box>
		</Modal>
	);
}

// Generate QR code URL manually since Amplify doesn't provide it directly
function buildTOTPQrCodeUrl(secretKey: string): string {
	return `otpauth://totp/VCG%20CRM:${encodeURIComponent(
		"user",
	)}?secret=${secretKey}&issuer=VCG%20CRM`;
}
